import type { Project } from './types';
import { batchFetchProjectTasks } from './stores/projectTasksCache';

/**
 * Creates expand/collapse state management for project chips.
 *
 * Manages a Set of expanded project IDs with support for:
 * - Single toggle (expand/collapse one project)
 * - Group expand/collapse (a subset of projects, e.g. per-week or per-status)
 * - Global collapse (clear all)
 * - Auto-initialization (expand all projects on first call)
 * - Batch task fetching on expand (for projects with tasks)
 *
 * @param getInitialProjects - Function returning projects to auto-expand on init.
 *   Called once during initializeExpansion(). For Rolling views: () => getAllProjects(currentPeriod).
 *   For Focus views: () => getAllProjects().
 */
export function useExpandableProjects(getInitialProjects: () => Project[]) {
  let expandedProjectIds = $state<Set<string>>(new Set());
  let hasInitialized = $state(false);

  /**
   * Auto-expand initial projects. Idempotent — only runs once.
   * Should be called from a $effect() in the consumer component.
   */
  function initializeExpansion() {
    if (hasInitialized) return;

    const projects = getInitialProjects();
    if (projects.length > 0) {
      expandedProjectIds = new Set(projects.map((p) => p.id));
    }

    hasInitialized = true;
  }

  /** Toggle a single project's expanded state. */
  function toggleProjectExpand(projectId: string) {
    const next = new Set(expandedProjectIds);
    if (next.has(projectId)) {
      next.delete(projectId);
    } else {
      next.add(projectId);
    }
    expandedProjectIds = next;
  }

  /** Check if a project is currently expanded. */
  function isProjectExpanded(projectId: string): boolean {
    return expandedProjectIds.has(projectId);
  }

  /**
   * Expand a group of projects. Batch-fetches tasks for those with task counts.
   * Used by Rolling views (per-period) and Focus views (per-status or global).
   */
  function expandGroup(projects: Project[]) {
    const next = new Set(expandedProjectIds);
    const projectsWithTasks: string[] = [];
    for (const p of projects) {
      next.add(p.id);
      if (p.taskCounts && p.taskCounts.total > 0) {
        projectsWithTasks.push(p.id);
      }
    }
    expandedProjectIds = next;
    if (projectsWithTasks.length > 0) {
      batchFetchProjectTasks(projectsWithTasks);
    }
  }

  /** Collapse a group of projects (remove them from expanded set). */
  function collapseGroup(projects: Project[]) {
    const projectIds = new Set(projects.map((p) => p.id));
    const next = new Set(expandedProjectIds);
    for (const id of projectIds) {
      next.delete(id);
    }
    expandedProjectIds = next;
  }

  /** Check if all projects in a group are expanded. Returns false for empty groups. */
  function areAllExpanded(projects: Project[]): boolean {
    if (projects.length === 0) return false;
    return projects.every((p) => expandedProjectIds.has(p.id));
  }

  /** Collapse everything — clears the entire expanded set. */
  function collapseAll() {
    expandedProjectIds = new Set();
  }

  return {
    get expandedProjectIds() {
      return expandedProjectIds;
    },
    initializeExpansion,
    toggleProjectExpand,
    isProjectExpanded,
    expandGroup,
    collapseGroup,
    areAllExpanded,
    collapseAll,
  };
}
